/**
 * Birthday Card Component
 * 
 * Displays a horizontal scrollable list of birthday cards for church members
 * with birthdays in the current month.
 * 
 * Features:
 * - Horizontal scrollable card layout
 * - Days until birthday countdown
 * - Contact information with phone and email icons
 * - User avatar/profile photo display
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  useTheme,
  Skeleton,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Cake as CakeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const BirthdayCard = ({ birthdays, loading, error }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const scrollContainerRef = React.useRef(null);
  const [expanded, setExpanded] = useState(false);

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -280, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 280, behavior: 'smooth' });
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const formatBirthday = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'dd/MM');
  };

  // Check if the birthday is today by comparing month and day
  const isBirthdayToday = (dateOfBirth) => {
    if (!dateOfBirth) return false;
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    
    return today.getDate() === birthDate.getDate() && 
           today.getMonth() === birthDate.getMonth();
  };

  return (
    <Card sx={{ 
      height: '100%', 
      bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : 'white', 
      overflow: 'hidden',
      maxWidth: '100%',
      width: '100%'
    }}>
      <CardContent sx={{ 
        p: 2, 
        overflow: 'hidden',
        maxWidth: '100%'
      }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 2,
            cursor: 'pointer'
          }}
          onClick={handleExpandClick}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CakeIcon color="primary" />
            <Typography variant="h6" sx={{ ml: 1 }}>
              {t('dashboard.birthdays')}
            </Typography>
          </Box>
          <IconButton
            aria-expanded={expanded}
            aria-label="show more"
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering the parent onClick
              handleExpandClick();
            }}
          >
            <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            flexWrap: 'wrap'
          }}>
            {loading ? (
              <Box sx={{ 
                width: '100%',
                display: 'flex', 
                gap: 2,
                overflow: 'hidden',
                flexDirection: 'row',
                flexWrap: 'wrap'
              }}>
                {[...Array(3)].map((_, i) => (
                  <Skeleton 
                    key={i} 
                    variant="rectangular" 
                    width="220px"
                    height={80} 
                    sx={{ 
                      borderRadius: 2, 
                      flexShrink: 0,
                      maxWidth: '220px',
                      minWidth: '220px'
                    }}
                  />
                ))}
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : birthdays.length === 0 ? (
              <Typography color="text.secondary">{t('dashboard.noBirthdays')}</Typography>
            ) : (
              <Box sx={{ 
                width: '100%', 
                maxWidth: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box
                  ref={scrollContainerRef}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 1,
                    width: '100%',
                    maxWidth: '100%',
                    scrollbarWidth: 'thin',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    '&::-webkit-scrollbar': { 
                      display: 'block',
                      height: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      borderRadius: '10px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
                      }
                    },
                    msOverflowStyle: 'auto'
                  }}
                >
                  {birthdays.map((user) => (
                    <Card
                      key={user._id}
                      sx={{
                        width: '220px',
                        minWidth: '220px',
                        height: '80px',
                        flexShrink: 0,
                        borderRadius: 2,
                        boxShadow: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{ display: 'flex', width: '100%', p: 1, alignItems: 'center' }}>
                        <Avatar
                          src={user.profilePhoto}
                          alt={`${user.firstName} ${user.lastName}`}
                          sx={{
                            width: 50,
                            height: 50,
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            mr: 2
                          }}
                        />
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                          <Typography variant="subtitle1" noWrap>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {formatBirthday(user.dateOfBirth)}
                          </Typography>
                          <Chip
                            label={
                              isBirthdayToday(user.dateOfBirth)
                                ? t('dashboard.birthdayToday')
                                : t('dashboard.daysUntilBirthday', { count: user.daysUntilBirthday })
                            }
                            color={isBirthdayToday(user.dateOfBirth) ? 'success' : 'primary'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                          {user.phone && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(`tel:${user.phone}`)}
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                              }}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          )}
                          {user.email && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(`mailto:${user.email}`)}
                              sx={{
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </Box>

                {birthdays.length > 1 && (
                  <>
                    <IconButton
                      onClick={handleScrollLeft}
                      sx={{
                        position: 'absolute',
                        left: { xs: -10, sm: -20 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: theme.palette.background.paper,
                        boxShadow: 2,
                        '&:hover': { bgcolor: theme.palette.background.paper },
                        display: { xs: 'none', sm: 'flex' },
                      }}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleScrollRight}
                      sx={{
                        position: 'absolute',
                        right: { xs: -10, sm: -20 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: theme.palette.background.paper,
                        boxShadow: 2,
                        '&:hover': { bgcolor: theme.palette.background.paper },
                        display: { xs: 'none', sm: 'flex' },
                      }}
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default BirthdayCard;
